/**
 * Iter 71b — Reservation Hold checkout page.
 *
 * Shown between clicking "Book" on `/cars/:id` and being redirected to
 * Stripe. Displays:
 *   • A big amber countdown timer synced to the backend `hold_expires_at`
 *     (survives page refresh — we always compute remaining time from the
 *     server timestamp, never a client-side interval).
 *   • Booking summary (car photo, dates, pickup/dropoff, totals).
 *   • Primary CTA "Pay Now" which redirects to the Stripe URL passed in
 *     via location.state, or fetched from the /bookings/checkout response
 *     stored in sessionStorage (survives Stripe redirect-back too).
 *   • Message reminding the customer their vehicle is only held for the
 *     20-minute window.
 *
 * If the hold expires, we swap the countdown for an "Expired" callout
 * and offer a "Start over" link back to the car detail page.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Clock, CheckCircle2, AlertTriangle, CalendarDays, MapPin, MapPinned, CreditCard, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";

const CHECKOUT_URL_KEY = (id) => `rentalcar:checkout_url:${id}`;

function _pad2(n) { return String(n).padStart(2, "0"); }

function _formatMMSS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${_pad2(mm)}:${_pad2(ss)}`;
}

function _formatDT(iso, time) {
  if (!iso) return "—";
  const d = new Date(iso);
  const datePart = d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  return time ? `${datePart} · ${time}` : datePart;
}

export default function ReservationCheckout() {
  const { bookingId } = useParams();
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();

  const [hold, setHold] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [redirecting, setRedirecting] = useState(false);

  // Grab the Stripe checkout URL that CarDetail stashed in sessionStorage
  // before navigating here. Persists across refresh + Stripe redirect back.
  const [checkoutUrl, setCheckoutUrl] = useState(() => {
    try { return sessionStorage.getItem(CHECKOUT_URL_KEY(bookingId)) || null; }
    catch { return null; }
  });

  // Fetch hold state from backend. Prefer JWT (from api client cookies /
  // Authorization header) — fall back to ?session_id= from the URL when
  // the Stripe redirect-back drops auth.
  const fetchHold = useMemo(() => async () => {
    const qs = urlParams.get("session_id");
    try {
      const { data } = await api.get(`/reservations/hold/${bookingId}`, {
        params: qs ? { session_id: qs } : undefined,
      });
      setHold(data);
      setError(null);
      return data;
    } catch (e) {
      setError(formatApiError(e));
      return null;
    }
  }, [bookingId, urlParams]);

  useEffect(() => {
    fetchHold();
    // Re-sync every 30s against the server so a stale client clock or
    // background tab doesn't drift the countdown by more than that.
    const poll = setInterval(fetchHold, 30_000);
    return () => clearInterval(poll);
  }, [fetchHold]);

  // Local 1s tick — purely visual. Never used for authoritative expiry
  // (we always compute remaining from `hold.hold_expires_at - now`).
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const secondsRemaining = useMemo(() => {
    if (!hold?.hold_expires_at) return 0;
    const exp = new Date(hold.hold_expires_at).getTime();
    return Math.max(0, Math.floor((exp - now) / 1000));
  }, [hold, now]);

  const isConfirmed = hold?.status === "confirmed";
  const isExpired = hold?.status === "expired" || (hold?.status === "active" && secondsRemaining === 0);
  const isActive = hold?.status === "active" && secondsRemaining > 0;

  // Auto-redirect to booking success once payment lands (webhook flips
  // status to confirmed). Uses a small delay so the user briefly sees
  // the "Confirmed" state before we forward them.
  const forwardedRef = useRef(false);
  useEffect(() => {
    if (isConfirmed && !forwardedRef.current) {
      forwardedRef.current = true;
      setTimeout(() => {
        const sid = hold?.session_id || "";
        navigate(`/booking/success?session_id=${encodeURIComponent(sid)}`);
      }, 1200);
    }
  }, [isConfirmed, hold, navigate]);

  const payNow = () => {
    if (!checkoutUrl) {
      setError("Payment link missing. Please start the booking again from the car page.");
      return;
    }
    setRedirecting(true);
    window.location.href = checkoutUrl;
  };

  const startOver = () => {
    try { sessionStorage.removeItem(CHECKOUT_URL_KEY(bookingId)); } catch { /* ignore */ }
    if (hold?.car?.id) navigate(`/cars/${hold.car.id}`);
    else navigate("/cars");
  };

  if (error && !hold) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto p-6 py-16">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6" data-testid="checkout-error">
            <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
              <AlertTriangle className="h-5 w-5" /> Unable to load your reservation
            </div>
            <div className="text-sm text-red-700">{error}</div>
            <Button className="mt-4" onClick={startOver} data-testid="checkout-error-restart">
              Return to browse
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!hold) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto p-6 py-16 text-neutral-500" data-testid="checkout-loading">
          Loading your reservation…
        </div>
      </div>
    );
  }

  const c = hold.car || {};
  const currency = (hold.currency || "eur").toUpperCase();
  const money = (n) => (n == null ? "—" : `${Number(n).toFixed(2)} ${currency}`);

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="max-w-4xl mx-auto p-4 sm:p-6 py-10" data-testid="checkout-page">
        {/* Countdown timer — first thing the customer sees.  */}
        <TimerBanner
          isActive={isActive}
          isExpired={isExpired}
          isConfirmed={isConfirmed}
          secondsRemaining={secondsRemaining}
          expiresAt={hold.hold_expires_at}
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
          {/* Booking summary card */}
          <div className="lg:col-span-3 rounded-2xl bg-white border border-neutral-200 shadow-sm p-5" data-testid="checkout-summary">
            <div className="flex items-center gap-4">
              {c.image_thumb && (
                <img
                  src={c.image_thumb}
                  alt={`${c.make} ${c.model}`}
                  className="w-24 h-16 object-cover rounded-lg border border-neutral-200"
                  data-testid="checkout-car-image"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500">Your vehicle</div>
                <div className="font-display text-xl font-medium truncate" data-testid="checkout-car-name">
                  {c.make} {c.model}
                  {c.year && <span className="text-neutral-500 font-normal"> · {c.year}</span>}
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SummaryRow
                icon={CalendarDays}
                label="Pick-up"
                value={_formatDT(hold.start_date, hold.pickup_time)}
                sub={hold.pickup_location}
                testId="checkout-pickup"
              />
              <SummaryRow
                icon={CalendarDays}
                label="Return"
                value={_formatDT(hold.end_date, hold.return_time)}
                sub={hold.dropoff_location}
                testId="checkout-return"
              />
              <SummaryRow
                icon={MapPin}
                label="Pick-up location"
                value={hold.pickup_location || "—"}
                testId="checkout-pickup-loc"
              />
              <SummaryRow
                icon={MapPinned}
                label="Drop-off location"
                value={hold.dropoff_location || "—"}
                testId="checkout-dropoff-loc"
              />
            </div>

            <div className="mt-6 border-t border-neutral-200 pt-4">
              <PriceRow label="Rental total" value={money(hold.rental_total)} />
              {hold.deposit > 0 && <PriceRow label="Refundable deposit" value={money(hold.deposit)} />}
              <PriceRow label="Paid now (platform fee)" value={money(hold.amount_paid_online)} strong />
              <PriceRow label="Due at pickup" value={money(hold.amount_due_at_pickup)} muted />
            </div>
          </div>

          {/* Payment CTA card */}
          <div className="lg:col-span-2 rounded-2xl bg-white border border-neutral-200 shadow-sm p-5" data-testid="checkout-payment-card">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2">
              Payment
            </div>
            {hold.offline_only ? (
              <div className="text-sm text-neutral-700 mb-3">
                No online payment required — you&apos;ll settle everything with the host at pickup. This reservation is already confirmed.
              </div>
            ) : (
              <>
                <div className="font-display text-2xl font-medium mb-1">{money(hold.amount_paid_online)}</div>
                <div className="text-xs text-neutral-500 mb-4">Refundable if the host cancels · Secure Stripe checkout</div>
                <Button
                  onClick={payNow}
                  disabled={!isActive || redirecting || !checkoutUrl}
                  className="w-full h-12 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-amber-400 font-bold text-base"
                  data-testid="checkout-pay-btn"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {redirecting ? "Redirecting…" : "Pay now"}
                  {!redirecting && <ArrowRight className="h-4 w-4 ml-2" />}
                </Button>
                <div className="text-[11px] text-neutral-500 mt-3 leading-snug">
                  Complete your payment before the timer expires to secure this vehicle.
                </div>
                {isExpired && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="checkout-expired-callout">
                    Your hold has expired. Another customer may now book these dates. Please start over to try again.
                    <Button
                      variant="outline"
                      onClick={startOver}
                      className="mt-2 w-full"
                      data-testid="checkout-restart-btn"
                    >
                      Start over
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TimerBanner({ isActive, isExpired, isConfirmed, secondsRemaining, expiresAt }) {
  if (isConfirmed) {
    return (
      <div
        className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 flex items-center gap-4"
        data-testid="checkout-timer-confirmed"
      >
        <CheckCircle2 className="h-8 w-8 text-emerald-600 shrink-0" />
        <div>
          <div className="font-semibold text-emerald-900">Payment received — booking confirmed!</div>
          <div className="text-sm text-emerald-800">Redirecting you to your booking details…</div>
        </div>
      </div>
    );
  }
  if (isExpired) {
    return (
      <div
        className="rounded-2xl bg-red-50 border border-red-300 p-5 flex items-center gap-4"
        data-testid="checkout-timer-expired"
      >
        <AlertTriangle className="h-8 w-8 text-red-600 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-red-900">Reservation hold expired</div>
          <div className="text-sm text-red-800">The 20-minute window elapsed. This vehicle is available for other customers again.</div>
        </div>
      </div>
    );
  }
  // Active countdown — highlight banner
  const pulse = isActive && secondsRemaining <= 120; // last 2 minutes → pulse warning
  return (
    <div
      className={`rounded-2xl border p-5 flex items-center gap-4 shadow-sm ${
        pulse
          ? "bg-red-50 border-red-300 animate-pulse"
          : "bg-amber-500 border-amber-600 text-neutral-900"
      }`}
      data-testid="checkout-timer-banner"
    >
      <Clock className={`h-10 w-10 shrink-0 ${pulse ? "text-red-700" : "text-neutral-900"}`} />
      <div className="min-w-0 flex-1">
        <div className={`text-xs uppercase tracking-[0.16em] font-bold ${pulse ? "text-red-800" : "text-neutral-800"}`}>
          Vehicle reserved for you
        </div>
        <div
          className={`font-display text-4xl sm:text-5xl font-medium tabular-nums leading-tight ${pulse ? "text-red-900" : "text-neutral-900"}`}
          data-testid="checkout-timer-value"
        >
          {_formatMMSS(secondsRemaining)}
        </div>
        <div className={`text-sm mt-1 ${pulse ? "text-red-800" : "text-neutral-800"}`}>
          Complete your payment before the timer expires to secure this vehicle.
        </div>
      </div>
      <div className="hidden sm:block text-right text-[11px] text-neutral-800/80 leading-tight">
        Hold expires<br />
        <span className="font-semibold" data-testid="checkout-timer-expiresat">
          {expiresAt ? new Date(expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
        </span>
      </div>
    </div>
  );
}

function SummaryRow({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <Icon className="h-4 w-4 text-neutral-500 mt-1 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500">{label}</div>
        <div className="text-sm font-semibold text-neutral-900 truncate">{value}</div>
        {sub && <div className="text-xs text-neutral-500 truncate">{sub}</div>}
      </div>
    </div>
  );
}

function PriceRow({ label, value, strong, muted }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${muted ? "text-neutral-500" : "text-neutral-900"}`}>
      <span className={strong ? "font-semibold" : "text-sm"}>{label}</span>
      <span className={strong ? "font-bold tabular-nums" : "tabular-nums text-sm"}>{value}</span>
    </div>
  );
}

export { CHECKOUT_URL_KEY };
